import React, { useState, useEffect, useCallback, useMemo } from "react";
import TopNav from "../../commonUI/TopHeader/TopHeader";
import {
  getQuotesRecordsCount,
  getretrieveQuotes,
} from "../../../services/jobService";
import { showSpinner, closeSpinner, modal, alert } from "../../../js/modules/Dialogs";
import { sessionExpired } from "../../../config/messages";
import { toScreen } from "../../../js/modules/Routing";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Pagination from "../../commonUI/Pagination/Pagination";
import SearchFilterBar from "../../commonUI/SearchFilterBar/SearchFilterBar";
import DataTable from "../../commonUI/DataTable/DataTable";
import TabNav from "../../commonUI/TabNav/TabNav";
import DateTime from "../../commonUI/DateTime/DateTime";

const JOB_TYPE_OPTIONS = [
  { value: "houseMove", label: "House Move" },
  { value: "sendItems", label: "Send Items" },
  { value: "wasteDisposal", label: "Waste Disposal" },
];

const COLUMNS = [
  { key: "driver",      label: "Driver",       sortable: true, sortKey: "name" },
  { key: "amount",      label: "Amount" },
  { key: "message",     label: "Message" },
  { key: "quotedOn",    label: "Quoted On",    sortable: true, sortKey: "jobDate" },
  { key: "jobType",     label: "Job Type" },
  { key: "jobLocation", label: "Job Location", sortable: true, sortKey: "location" },
  { key: "jobCreated",  label: "Job Created",  sortable: true, sortKey: "creationDate" },
  { key: "actions",     label: "Actions" },
];

const Quotes = ({ userDetails }) => {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState("");
  const [search, setSearch]           = useState("");
  const [pageSize, setPageSize]       = useState(15);
  const [totalPages, setTotalPages]   = useState(1);
  const [page, setPage]               = useState(1);
  const [inputValue, setInputValue]   = useState(1);
  const [quoteDate, setQuoteDate]     = useState({ start: null, end: null });
  const [jobDate, setJobDate]         = useState({ start: null, end: null });
  const [jobType, setJobType]         = useState(null);
  const [filter, setFilter]           = useState(
    window.history.state?.usr?.selectedFilter ?? "open"
  );
  const [sort, setSort]       = useState("");
  const [quotes, setQuotes]   = useState([]);
  const [activeTab, setActiveTab] = useState("open");
  const [activeFilters, setActiveFilters] = useState({
    open: null, paid: null, complete: null, closed: null,
  });
  const [filterCount, setFilterCount] = useState({
    total: 0, open: 0, closed: 0, agreed: 0, paid: 0, complete: 0,
  });

  const getQuotes = async (event) => {
    if (event) event.preventDefault();
    showSpinner();
    try {
      const queryParams = {
        search,
        filter,
        sort,
        page,
        limit: pageSize,
        ...(activeFilters[activeTab] && { customFilter: activeFilters[activeTab] }),
      };
      const resJson = await getretrieveQuotes(queryParams);
      closeSpinner();
      setTotalPages(Math.ceil(resJson?.totalRecords / pageSize));
      setQuotes(resJson?.data || []);
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        setQuotes([]);
        alert(error.message || "Failed to fetch quotes");
      }
    }
  };

  const fetchFilterCount = useCallback(async () => {
    showSpinner();
    try {
      const resJson = await getQuotesRecordsCount();
      closeSpinner();
      setFilterCount({
        total:    resJson?.totalDocs,
        open:     resJson?.openQuotes,
        closed:   resJson?.closedQuotes,
        paid:     resJson?.paidQuotes,
        complete: resJson?.completedQuotes,
      });
    } catch (error) {
      closeSpinner();
      if (error.response?.status === 401) {
        modal(sessionExpired);
        toScreen(false, navigate, "/logout");
      } else {
        alert(error.message || "Failed to fetch filter count");
      }
    }
  }, [navigate]);

  const handleSearch = useCallback(() => {
    setSearch(searchValue);
    setFilter(searchValue === "" ? "open" : null);
    setPage(1);
  }, [searchValue]);

  const sortQuotes = useCallback(
    (event, field) => {
      if (event) event.preventDefault();
      const sortFields = {
        name:         { asc: "name=1",         desc: "name=-1" },
        jobDate:      { asc: "jobDate=1",      desc: "jobDate=-1" },
        creationDate: { asc: "creationDate=1", desc: "creationDate=-1" },
        location:     { asc: "location=1",     desc: "location=-1" },
      };
      const currentSort = sortFields[field];
      if (!currentSort) return;
      const newSort = sort === currentSort.asc ? currentSort.desc : currentSort.asc;
      setSearch("");
      setSort(newSort);
      setPage(1);
    },
    [sort]
  );

  const getSortClass = useCallback(
    (key) => {
      if (sort === `${key}=1`)  return "sort-button sorting-icon sorting-icon-up";
      if (sort === `${key}=-1`) return "sort-button sorting-icon sorting-icon-down";
      return "sort-button";
    },
    [sort]
  );

  const renderRow = useCallback(
    (quote, index) => (
      <tr key={index}>
        <td>
          <div className="d-flex align-items-center g-12">
            <img
              src={quote?.accountInfo?.avatar || "defaultAvatarImage"}
              alt="avatar"
              className="avatar-pic"
            />
            <div className="text-capitalize">
              {quote?.firstname || "-"}
              <br />
              <span className="text-sm text-gray-600">{quote?.shortId}</span>
            </div>
          </div>
        </td>
        <td>{"£220.00"}</td>
        <td>{"I can help with your move."}</td>
        <td>
          <DateTime date={quote?.jobDate} />
        </td>
        <td>{"House Move"}</td>
        <td>
          <span className="text-capitalize">
            {`${quote?.originAddress || ""}${
              quote?.originAddress || quote?.destinationAddress ? " → " : ""
            }${quote?.destinationAddress || ""}`}
          </span>
        </td>
        <td>
          <DateTime date={quote?.createdAt} />
        </td>
        <td>
          <div className="d-inline-flex align-items-center g-8">
            <button type="button" className="custom-btn">
              View Job
            </button>
            <button
              type="button"
              className="custom-btn"
              onClick={(e) => toScreen(e, navigate, "/quote/" + quote?.shortId)}
            >
              View Quote
            </button>
          </div>
        </td>
      </tr>
    ),
    [navigate]
  );

  const handleReset = () => {
    setActiveTab("open");
    setSort("");
    setSearch("");
    setSearchValue("");
    setJobType(null);
    setQuoteDate({ start: null, end: null });
    setJobDate({ start: null, end: null });
    setActiveFilters({ open: null, paid: null, complete: null, closed: null });
    setFilter("open");
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPage(1);
    setFilter(tab);
  };

  const tabs = useMemo(
    () => [
      { key: "open",     label: "Open",     count: filterCount?.open     || 0 },
      { key: "paid",     label: "Paid",     count: filterCount?.paid     || 0 },
      { key: "complete", label: "Complete", count: filterCount?.complete || 0 },
      { key: "closed",   label: "Closed",   count: filterCount?.closed   || 0 },
    ],
    [filterCount]
  );

  const filters = useMemo(
    () => [
      {
        type:        "search",
        key:         "search",
        label:       "Search",
        value:       searchValue,
        onChange:    setSearchValue,
        placeholder: "Search...",
        onSubmit:    handleSearch,
      },
      {
        type:        "select",
        key:         "jobType",
        label:       "Job Type",
        value:       jobType,
        onChange:    setJobType,
        placeholder: "All Types",
        options:     JOB_TYPE_OPTIONS,
      },
      {
        type:        "dateRange",
        key:         "quoteDate",
        label:       "Quote Date",
        value:       quoteDate,
        onChange:    setQuoteDate,
        placeholder: "Anytime",
      },
      {
        type:        "dateRange",
        key:         "jobDate",
        label:       "Job Date",
        value:       jobDate,
        onChange:    setJobDate,
        placeholder: "Anytime",
      },
    ],
    [searchValue, jobType, quoteDate, jobDate, handleSearch]
  );

  useEffect(() => {
    setInputValue(page);
  }, [page]);

  useEffect(() => {
    getQuotes();
  }, [filter, sort, page, pageSize, search]);

  useEffect(() => {
    fetchFilterCount();
  }, []);

  return (
    <>
      <TopNav title="Quotes" />
      <div className="global-body">
        <div className="global-body-container">
          <TabNav tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

          <SearchFilterBar filters={filters} onReset={handleReset} />

          <DataTable
            data={quotes}
            columns={COLUMNS}
            renderRow={renderRow}
            onSort={sortQuotes}
            getSortClass={getSortClass}
            emptyMessage="No quotes found"
            tableClassName="table clientTable"
            colSpan={8}
          />

          <Pagination
            pageSize={pageSize}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            inputValue={inputValue}
            setInputValue={setInputValue}
            setPageSize={setPageSize}
          />
        </div>
      </div>
    </>
  );
};

Quotes.propTypes = {
  userDetails: PropTypes.object.isRequired,
};

export default Quotes;
